import { observer } from "mobx-react-lite";
import { useApp } from "../../context";

import {
  Box,
  Button,
  Card,
  Grid,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";

import { Checkbox, Form } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

const useStyles = makeStyles((theme) => ({
  marginBottom: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  padding: {
    padding: theme.spacing(2),
  },
  hero: {
    textAlign: "center",
  },
  modeCard: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    textAlign: "center",
    height: "100%",
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
  },
  modeEmoji: {
    fontSize: 40,
    lineHeight: 1,
  },
}));

export const Home = observer(function Home() {
  const app = useApp();
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const startMode = async (mode: "analytic" | "game" | "daily" | "survival") => {
    if (!app.benchmarks.isModeEnabled(mode)) {
      return;
    }

    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (mode === "analytic") {
      await app.benchmarks.createBenchmark();
      navigate("/benchmark/analytic");
    } else if (mode === "game") {
      navigate("/benchmark/game");
    } else if (mode === "survival") {
      navigate("/benchmark/survival");
    } else {
      navigate("/benchmark/daily");
    }
  };

  return (
    <Card>
      <Grid container justifyContent="center" spacing={1}>
        <Grid item xs={12}>
          <div className={classes.hero}>
            <Typography
              variant="h2"
              component="h1"
              className={classes.marginBottom}
              style={{ marginBottom: 10 }}
            >
              {t("greeting-title")}
            </Typography>
            <Typography
              variant="body1"
              className={classes.marginBottom}
              style={{ marginBottom: 24 }}
            >
              {t("greeting-subtitle")}
            </Typography>

            <Form form={form} layout="vertical">
              <Form.Item
                name="consent"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Bitte bestätigen Sie die Datenschutzerklärung, um fortzufahren.",
                            ),
                          ),
                  },
                ]}
              >
                <Checkbox>
                  Ich habe die{" "}
                  <Link to="/privacy-policy-game">Datenschutzerklärung</Link>{" "}
                  zur Durchführung des Spiels zur Kenntnis genommen.
                </Checkbox>
              </Form.Item>
            </Form>

            <Grid container spacing={2} style={{ marginTop: 8, paddingLeft: 16, paddingRight: 16 }}>
              {app.benchmarks.isModeEnabled("analytic") && (
                <Grid item xs={12} md={6}>
                  <Box className={classes.modeCard}>
                    <span className={classes.modeEmoji}>🔬</span>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Analytischer Modus
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Bewerte KI-Stimmen anhand von 8 Kriterien. Deine Antworten helfen der Wissenschaft.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => startMode("analytic")}
                    >
                      Starten
                    </Button>
                  </Box>
                </Grid>
              )}
              {app.benchmarks.isModeEnabled("game") && (
                <Grid item xs={12} md={6}>
                  <Box className={classes.modeCard}>
                    <span className={classes.modeEmoji}>🎮</span>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Spielmodus
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Erkenne KI-Stimmen so schnell wie möglich. Punkte basierend auf Richtigkeit und Reaktionszeit.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => startMode("game")}
                    >
                      Spielen
                    </Button>
                  </Box>
                </Grid>
              )}
              {app.benchmarks.isModeEnabled("daily") && (
                <Grid item xs={12} md={6}>
                  <Box className={classes.modeCard}>
                    <span className={classes.modeEmoji}>📅</span>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Scamdle
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      5 Stimmen, täglich neu. Alle spielen dieselben Clips — vergleiche dich im Leaderboard.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => startMode("daily")}
                    >
                      Scamdle spielen
                    </Button>
                  </Box>
                </Grid>
              )}
              {app.benchmarks.isModeEnabled("survival") && (
                <Grid item xs={12} md={6}>
                  <Box className={classes.modeCard}>
                    <span className={classes.modeEmoji}>❤️</span>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Survival
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      3 Leben, endlose Fragen. Wie weit kommst du, bevor du alle Leben verlierst?
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => startMode("survival")}
                    >
                      Survival starten
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>

            <hr style={{ margin: "32px 0" }} />
            <Typography variant="body1" className={classes.marginBottom}>
              {t("data-donation-message")}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              className={classes.button}
              onClick={() => navigate("/data-donation")}
            >
              {t("button-data-donation")}
            </Button>
          </div>
        </Grid>

        {app.report.lastReport && (
          <Grid item xs={12}>
            <Paper>
              <List>
                <ListItem
                  button
                  onClick={() => {
                    if (app.report.lastReport) {
                      const { id, secret } = app.report.lastReport;
                      navigate(`/report/${id}/${secret}`);
                    }
                  }}
                >
                  <ListItemText primary={t("last-report")} />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => {
                        if (app.report.lastReport) {
                          const { id, secret } = app.report.lastReport;
                          navigate(`/report/${id}/${secret}`);
                        }
                      }}
                    >
                      {t("button-open-report")}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Card>
  );
});
